/**
 * In-App Review Utility
 * Handles triggering native in-app review prompts at the right time.
 * 
 * Rules:
 * - Trigger after daily_reward_streak >= 3 (user engaged for 3 days)
 * - Only show once every 14 days
 * - Use native OS review APIs (SKStoreReviewController / Google In-App Review)
 */

import { Capacitor } from "@capacitor/core";

const REVIEW_COOLDOWN_DAYS = 14;

/**
 * Check if user is eligible for review prompt
 * @param {object} user - User object with last_review_prompt and daily_reward_streak
 */
export const shouldPromptReview = (user) => {
  if (!user) return false;
  
  // Need at least 3 day streak
  const streak = user.daily_reward_streak || 0;
  if (streak < 3) return false;
  
  // Check cooldown
  const lastPrompt = user.last_review_prompt;
  if (lastPrompt) {
    const lastPromptDate = new Date(lastPrompt);
    const now = new Date();
    const daysSincePrompt = Math.floor((now - lastPromptDate) / (1000 * 60 * 60 * 24));
    if (daysSincePrompt < REVIEW_COOLDOWN_DAYS) return false;
  }
  
  return true;
};

/**
 * Trigger native in-app review
 * @returns {Promise<boolean>} - True if review was shown, false otherwise
 */
export const triggerInAppReview = async () => {
  if (!Capacitor.isNativePlatform()) {
    console.log("In-app review only available on native platforms");
    return false;
  }
  
  try {
    const { InAppReview } = await import("@capacitor-community/in-app-review");
    await InAppReview.requestReview();
    return true;
  } catch (err) {
    console.error("Failed to trigger in-app review:", err);
    return false;
  }
};

/**
 * Record that review prompt was shown
 * @param {function} updateUserApi - Function to call API to update user
 */
export const recordReviewPrompt = async (apiUrl, authHeader) => {
  try {
    await fetch(`${apiUrl}/users/review-prompt`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeader
      }
    });
  } catch (err) {
    console.error("Failed to record review prompt:", err);
  }
};

export default {
  shouldPromptReview,
  triggerInAppReview,
  recordReviewPrompt,
};
